import api from '@api/api';
import authApi from '@api/authApi';
import connection from '@api/connection';
import CrudApi from '@api/crudApi';
import userApi from '@api/userApi';
import { Location } from '@models/location';
import type { Maintenance } from '@models/maintenance';
import fetchPages from '@util/fetchPages';
import service from '@util/service';

export default service(
  'locationApi',
  class LocationApi extends CrudApi<Location> {
    path = 'v1/locations';
    model = Location;

    live = connection<Location[], []>(
      () => ({
        fetch: () =>
          fetchPages(Location, (skip, limit) => api.get(this.path, { searchParams: { skip, limit } }).json()),
        scope: 'global',
        handlers: {
          locationCreated(payload, { updateValue }) {
            const location = Location.parse(payload);

            updateValue((locations) => [...locations, location]);
          },
          locationUpdated(payload, { updateValue }) {
            const location = Location.parse(payload);

            updateValue((locations) =>
              locations.map((loc) => {
                if (loc.id === location.id) {
                  return location;
                }
                return loc;
              }),
            );
          },
          locationDeleted(payload, { updateValue }) {
            const location = Location.parse(payload);

            updateValue((locations) => locations.filter((loc) => loc.id !== location.id));
          },
        },
      }),
      {
        resourceGroup: this.caches,
      },
    );

    get = connection<Location, [locationId: string]>((locationId) => ({
      fetch: () => api.get(`${this.path}/${locationId}`).json(),
      scope: locationId,
      handlers: {
        locationUpdated(payload, { updateValue }) {
          const location = Location.parse(payload);

          updateValue(location);
        },
      },
    }));

    async delete(id: string): Promise<void> {
      await super.delete(id);

      userApi.caches.invalidateAll();
      authApi.me.invalidate();
    }

    async setMaintenance(locationId: string, maintenance: Maintenance) {
      const versionBefore = this.get(locationId).state.get().value?.v;

      await api.post(`${this.path}/maintain/${locationId}`, {
        json: maintenance,
      });

      await this.whenUpdated(locationId, versionBefore);
    }

    async whenUpdated(locationId: string, version: number | undefined | null) {
      return await this.get(locationId).state.once((state) => {
        return typeof state.value?.v === 'number' && state.value.v > (version ?? 0);
      });
    }
  },
);
